import { useMemo } from 'react';

import type { AdditionalFilterInDropdown, SelectedFiltersValues } from '../AdditionalFilters/constants';
import { mapFilterConfigsToAdditionalFilters } from '../AdditionalFilters/hooks/mapFilterConfigToAdditionalFilter';
import { useGetTraffickingFilterConfig } from '../AdditionalFilters/hooks/useGetTraffickingFilterConfig';
import { getSortedFilters, setSearchParamsForIncludeExcludeDropdowns } from '../AdditionalFilters/utils';

// These filter IDs have dedicated permanent controls rendered directly
// in AppliedFiltersBar (topBarFilters section). They must not appear in the
// additional-filters chip registry to prevent duplicate display.
export const PERMANENT_USER_FILTER_IDS = new Set(['traffickerEmailList', 'accountExecutiveList']);

/**
 * Builds the list of `AdditionalFilterInDropdown` definitions from the
 * config-driven GQL catalog for downstream chip rendering.
 *
 * Filters whose IDs are in `PERMANENT_USER_FILTER_IDS` are always excluded
 * because they already have dedicated display in the applied-filters bar.
 */
export const useAdditionalFilterDefinitions = (
  selectedFiltersValues: SelectedFiltersValues
): AdditionalFilterInDropdown[] => {
  const { filterConfigs } = useGetTraffickingFilterConfig();

  return useMemo(() => {
    const configDrivenFilters = mapFilterConfigsToAdditionalFilters(filterConfigs)
      .filter((f) => !PERMANENT_USER_FILTER_IDS.has(f.searchParam))
      .map((filter) => ({
        ...filter,
        optionsSource: 'config' as const,
      }));

    const sortedFilters = getSortedFilters(configDrivenFilters);
    return setSearchParamsForIncludeExcludeDropdowns(sortedFilters, selectedFiltersValues);
  }, [selectedFiltersValues, filterConfigs]);
};
